import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { compressForUpload } from '../../../../utils/imageCompression';

// Sentry
import * as Sentry from '@sentry/react';

import {
  useForm,
  Controller,
  useWatch,
  ControllerProps,
} from 'react-hook-form';
import { useDispatch } from 'react-redux';

// Components
import { Typography } from '../../../display/Typography';
import { MainButton } from '../../../inputs/buttons/MainButton';
import DropDownInput from '../../../inputs/DropDown/DropDownInput';
import SearchableDropdown from '../../../inputs/SearchableDropdown';
import SimpleSwitch from '../../../inputs/buttons/SwitchButton/Simple';
import { InputTextArea } from '../../../inputs/InputTextArea';
import { DateTime } from '../../../inputs/DateTime';
import { HelpTooltip } from '../../../display/HelpTooltip';
import { ButtonIconBackground } from '../../../inputs/buttons/ButtonIconBackground';
import { InputLoadImages } from '../../../inputs/InputLoadImages';
import { InputLoadFiles } from '../../../inputs/InputLoadFiles';
import { Loader } from '../../../inputs/Loader';

// Styles
import {
  GeneralContainer,
  ButtonContainer,
  Row,
  Title,
  InlineLoaderSlot,
} from '../styles';
import {
  AddImagesButton,
  BasicScheduleActions,
  BasicScheduleContainer,
  HiddenInput,
  ImagesContainer,
  SketchContainer,
  SketchImagesContainer,
  ContainerInputLoads,
  InputsContainer,
  NormalContainer,
  RowsContainerLeft,
  RowsContainerRight,
  SwitchContainer,
} from './styles';

// Types
import {
  type ActionEditionTypes,
  type ActionEditionFormValues,
  type Img,
  type Sketch,
  CustomFile,
} from './types';
import { OrderState, OrderType } from '../../../../types/orders';
import { DataState } from '../types';

import { type WorkOrder } from '../../../../types/workOrder';
import { type OptionSelected } from '../../../inputs/DropDown/DropDownList/types';
import { type ImperativeRefSearchableDropdown } from '../../../inputs/SearchableDropdown/types';

// Utils
import { useCacheFormInSessionStorage } from '../../../../hooks/useCacheForm';
import { closeModal, openModal } from '../../../../state/actions/ui/modal';
import { modalIds } from '../../../modals/SuperModal/types';
import {
  deleteFilesFromDirectory,
  getFilesFromSessionStorage,
  saveFilesIntoADirectory,
  dataURItoBlob,
  generateFileName,
} from '../../../../utils/files';
import {
  compressSessionStorage,
  decompressSessionStorage,
} from '../../../../utils/sessionStorage';
import { yupResolver } from '@hookform/resolvers/yup';
import { EditingActionSchema } from './schema';
import {
  type EditingActionSchemaContext,
  isBasicScheduleEditable,
  sameScheduleValue,
} from './basicSchedule';
import { requestBasicScheduleRecalculation } from './recalculateBasicSchedule';
import {
  showBackendErrorToast,
  showErrorToast,
  showSuccessToast,
} from '../../../../utils/toasts';
import { useIsLandscapeTablet } from '../../../../hooks/useMediaBreakPoints';

// GQL
import {
  GET_CAPITOL_BY_ID,
  GET_ELEMENT_ORDERS_BY_ID,
  GET_ELEMENT_ORDER_BY_ID,
  GET_SUB_CAPITOLS_BY_IDS,
  GET_SUB_CAPITOL_BY_ID,
} from '../../../../gql/queries/orders/predefinedData';
import { client } from '../../../../gql/client';
import { GET_ACTIVITY_BY_ID } from '../../../../gql/queries/actions/activitys';

// Constants
import { formActionNaming, directoryNaming } from '../constants';
import {
  removeDraftSketchFile,
  saveDraftSketchFile,
  sketchFileNameMarker,
} from '../../../../utils/sketchFile';
import { screenImageSource } from '../../../../offline/formStorage';
import { fetchDefaultData } from '../../../../utils/fetchData';
import Debug from 'debug';
import {
  restoreSketchToSessionStorage,
  clearSketchFromSessionStorage,
} from '../../../../utils/sketch';

const log = Debug('rois:sketch:action');
const enableSketchDebug = () => Debug.enable('rois:sketch:*');

const EditingActionForm = ({
  onFormSubmit,
  initialData,
  namingPersistForm,
  namingUpdateForm,
  textButton = 'Continuar',
  basicSchedule,
}: ActionEditionTypes) => {
  const { t } = useTranslation('forms');
  const initialFiles = getFilesFromSessionStorage(
    formActionNaming.SELECTED_ACTIONS_FILES
  )?.map((file) => ({ file: file, id: file.name }));
  const [switchButtonState, setSwitchButtonState] = useState<boolean>(false);
  const [selectedImages, setSelectedImages] = useState<Img[]>([]);
  const [sketch, setSketch] = useState<Sketch | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<CustomFile[]>(
    initialFiles ? initialFiles : []
  );
  const [isLoadingFiles, setIsLoadingFiles] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  // ✅ Para activar validación visual al navegar desde modal de errores
  const locationState = useLocation();
  // Horario de la básica: editable solo para gestión y sin anular. Manda en
  // los campos y en la regla fin ≥ inicio (revisión 12-oct, R2): con los
  // campos deshabilitados esa regla no puede bloquear el avance.
  const basicScheduleEditable = isBasicScheduleEditable(
    !!basicSchedule?.editable,
    initialData?.state,
    initialData?.parentExpeditionOrderStatus
  );

  const {
    control,
    handleSubmit,
    register,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<ActionEditionFormValues & WorkOrder, EditingActionSchemaContext>({
    defaultValues: {
      ...initialData,
      editionDescription:
        initialData.editionDescription ?? initialData.description,
      editionAssetSwitch: initialData?.editionAssetSwitch
        ? initialData.editionAssetSwitch
        : 'false',
      editionCapitol: initialData.editionCapitol
        ? initialData.editionCapitol
        : initialData.capitol,
      editionSubCapitol: initialData.editionSubCapitol
        ? initialData.editionSubCapitol
        : initialData.subCapitol,
      editionLaneCutting: initialData.editionLaneCutting
        ? initialData.editionLaneCutting
        : 'false',
      editionElementOrder: initialData.editionElementOrder
        ? initialData.editionElementOrder
        : initialData?.actionElementOrder,
      editionActivity: initialData.editionActivity
        ? initialData.editionActivity
        : initialData?.actionActivity,
      editionPredefine: initialData.editionPredefine ?? initialData.predefine,
    },
    resolver: yupResolver(EditingActionSchema) as any,
    context: { basicScheduleEditable },
  });

  // Horas del horario de la básica (bug 12-oct, captura del usuario): al
  // cambiar una, se revalida el bloque. Entrando desde el aviso de errores
  // (`validateOnMount` → `trigger()`, sin submit) RHF no revalida al cambiar y
  // el texto rojo se quedaba hasta el siguiente clic aunque ya valiera. Solo
  // con un cambio de verdad: `DateTime` avisa también al montarse con la
  // misma hora, y eso no debe pintar errores.
  const onBasicScheduleChange =
    (onChange: (value: string) => void, current: unknown) =>
    (date?: string) => {
      const next = date ?? '';
      onChange(next);
      if (!sameScheduleValue(next, current)) {
        void trigger(['registerInitDate', 'registerArrivalDate', 'registerEndDate']);
      }
    };

  // «Recalcular con el recorrido» (12-oct): el back recalcula inicio y fin
  // desde el hueco del tramo y quita las marcas de «puesto a mano». Lo devuelto
  // ya está en el servidor, así que pasa también a la base del diff
  // (`dataFromServer`): si no, el envío lo mandaría como un cambio a mano.
  // `scheduleVersion` remonta los `DateTime`, que solo leen la hora al montar.
  const [scheduleVersion, setScheduleVersion] = useState(0);
  const [isRecalculatingSchedule, setIsRecalculatingSchedule] = useState(false);
  const recalculateOrderId = basicScheduleEditable
    ? basicSchedule?.recalculateOrderId
    : undefined;
  const onRecalculateSchedule = async () => {
    if (!recalculateOrderId) return;
    setIsRecalculatingSchedule(true);
    try {
      const recalculated =
        await requestBasicScheduleRecalculation(recalculateOrderId);
      const setFormValue = setValue as unknown as (
        name: string,
        value: unknown
      ) => void;
      setFormValue('registerInitDate', recalculated.registerInitDate);
      setFormValue('registerEndDate', recalculated.registerEndDate);
      const savedFromServer = (getValues() as Record<string, unknown>)
        .dataFromServer as Record<string, unknown> | undefined;
      if (savedFromServer) {
        setFormValue('dataFromServer', { ...savedFromServer, ...recalculated });
      }
      setScheduleVersion((version) => version + 1);
      void trigger(['registerInitDate', 'registerArrivalDate', 'registerEndDate']);
      showSuccessToast(t('toasts:success.basicScheduleRecalculated'));
    } catch (error) {
      // Sin red no se encola (es una acción del momento): se avisa y ya.
      const offline =
        !navigator.onLine ||
        !!(error as { networkError?: unknown } | null)?.networkError;
      if (offline) {
        showErrorToast(t('toasts:error.network'));
      } else {
        showBackendErrorToast(error, t('toasts:error.generic'));
      }
    } finally {
      setIsRecalculatingSchedule(false);
    }
  };

  // References
  const capitolDropDownRef = useRef<ImperativeRefSearchableDropdown | null>(
    null
  );
  const subCapitolDropDownRef = useRef<ImperativeRefSearchableDropdown | null>(
    null
  );
  const elementOrdersDropDownRef =
    useRef<ImperativeRefSearchableDropdown | null>(null);
  const activitysDropDownRef = useRef<ImperativeRefSearchableDropdown | null>(
    null
  );
  const predefineDropDownRef = useRef<ImperativeRefSearchableDropdown | null>(
    null
  );
  // Watchers
  const subCapitols = useWatch({
    control,
    name: 'subCapitols',
  });

  const elementOrders = useWatch({
    control,
    name: 'elementOrders',
  });

  useCacheFormInSessionStorage(namingPersistForm, control);

  // ✅ Activar validación visual al navegar desde modal de errores
  useEffect(() => {
    if (locationState.state?.validateOnMount) {
      console.log('🚨 ActionEditionForm: Triggering validation on mount');
      trigger();
    }
  }, [locationState.state?.validateOnMount, trigger]);

  // NOTE: previously a useEffect aquí auto-disparaba la cascada del Predefinido
  // al montar (llamando a predefineDropDownRef.current?.setSelectedOption(...)).
  // Eso sobrescribía los valores cacheados de Capítulo/Subcap/Elem/Acción tras
  // F5 o cambio de pestaña, porque setSelectedOption dispara onChangeSelected ->
  // onSelectPredefined -> cascada completa.
  // Tras el fix Bug #2 (selectedOption={value} en los 5 Controllers), la
  // visualización del Predefinido y de los campos cascadeados se hidrata
  // automáticamente desde el estado de RHF (que useCacheForm restaura desde
  // sessionStorage). La cascada solo debe dispararse cuando el usuario
  // selecciona un Predefinido nuevo.

  useLayoutEffect(() => {
    if (initialData?.editionImages) {
      const parsedArray: Img[] = JSON.parse(initialData?.editionImages);
      setSelectedImages(parsedArray);
    }
    if (initialData?.editionSketch) {
      enableSketchDebug();
      const parsedSketch = JSON.parse(initialData.editionSketch);
      log('📋 Parsed sketch from initialData: %O', {
        id: parsedSketch.id,
        databaseId: parsedSketch.databaseId,
        name: parsedSketch.name,
        hasDate: !!parsedSketch.date,
        hasCreatingDate: !!parsedSketch.creatingDate,
      });

      // La marca `fileName` (el nombre de su fichero, o `null` si no tiene) se
      // conserva al volver a la pestaña: si se perdiera, el efecto que reescribe
      // el campo dejaría el borrador sin marca y el alta lo trataría como de
      // antes del cambio (`sketchFile.ts`).
      const fileNameMarker = sketchFileNameMarker(parsedSketch);
      if (parsedSketch?.name) {
        setSketch({
          id: parsedSketch.id,
          databaseId: parsedSketch.databaseId, // T56 FIX
          name: parsedSketch.name,
          date: new Date(
            parsedSketch?.date ?? parsedSketch?.creatingDate
          ).toISOString(),
          road: parsedSketch?.road ?? initialData?.road ?? '',
          ...fileNameMarker,
        });
      } else {
        setSketch({
          id: parsedSketch.id,
          databaseId: parsedSketch.databaseId, // T56 FIX
          date: new Date(
            parsedSketch?.date ?? parsedSketch?.creatingDate
          ).toISOString(),
          road: parsedSketch?.road ?? initialData?.road ?? '',
          ...fileNameMarker,
        });
      }
    }
    setSwitchButtonState(switchStateValue());
  }, []);

  useEffect(() => {
    const files = selectedFiles.map((file) => file.file);
    saveFilesIntoADirectory(OrderType.Action, files);
  }, [selectedFiles, selectedImages]);

  useEffect(() => {
    const imagesValue =
      selectedImages.length > 0 ? JSON.stringify(selectedImages) : null;
    setValue('editionImages', imagesValue);
  }, [selectedImages]);

  useEffect(() => {
    const sketchValue = sketch ? JSON.stringify(sketch) : null;
    setValue('editionSketch', sketchValue);
  }, [sketch]);

  const isLandscapeTablet = useIsLandscapeTablet();

  const onSelectPredefined = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        onChange(option?.value ?? '');

        const [
          {
            data: { capitol },
          },
          {
            data: { subCapitol },
          },
          {
            data: { elementOrder },
          },
        ] = await Promise.all([
          client.query({
            query: GET_CAPITOL_BY_ID,
            variables: {
              id: option?.data.capitolId,
            },
          }),
          client.query({
            query: GET_SUB_CAPITOL_BY_ID,
            variables: {
              id: option?.data.subCapitolId,
            },
          }),
          client.query({
            query: GET_ELEMENT_ORDER_BY_ID,
            variables: {
              id: option?.data.elementOrderId,
            },
          }),
        ]);

        const { readSubCapitols } = client.readQuery({
          query: GET_SUB_CAPITOLS_BY_IDS,
          variables: {
            ids: capitol?.subCapitolsIDs ?? [],
          },
        });

        if (readSubCapitols.length === 0) {
          await fetchDefaultData();
          const { readSubCapitols } = client.readQuery({
            query: GET_SUB_CAPITOLS_BY_IDS,
            variables: {
              ids: capitol?.subCapitolsIDs ?? [],
            },
          });
          setValue('subCapitols', readSubCapitols);
        } else {
          setValue('subCapitols', readSubCapitols);
        }

        capitolDropDownRef.current?.setSelectedOption({
          label: capitol.name,
          value: capitol.id,
          data: capitol.subCapitolsIDs,
        });

        subCapitolDropDownRef.current?.setSelectedOption({
          label: subCapitol.name,
          value: subCapitol.id,
          data: subCapitol.elementOrdersIds,
        });

        const { readElementOrders } = client.readQuery({
          query: GET_ELEMENT_ORDERS_BY_ID,
          variables: {
            ids: subCapitol?.elementOrdersIds ?? [],
          },
        });

        setValue('elementOrders', readElementOrders);

        elementOrdersDropDownRef.current?.setSelectedOption({
          label: elementOrder.name,
          value: elementOrder.id,
          data: elementOrder.subCapitolsIDs,
        });

        // Guard: Activity opcional en el predefinido. Si no la trae, dejar el
        // desplegable vacío (la validación `required` obliga a elegirla a mano).
        // Evita romper GET_ACTIVITY_BY_ID (id requerido) con un predefinido sin activity.
        if (option?.data.activityId) {
          const {
            data: { activity },
          } = await client.query({
            query: GET_ACTIVITY_BY_ID,
            variables: { id: option.data.activityId },
          });
          activitysDropDownRef.current?.setSelectedOption({
            label: activity.name,
            value: activity.id,
          });
        } else {
          activitysDropDownRef.current?.clearSelected();
          setValue('editionActivity', '');
        }
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    [
      capitolDropDownRef,
      subCapitolDropDownRef,
      elementOrdersDropDownRef,
      activitysDropDownRef,
    ]
  );

  const onSelectCapitol = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        try {
          const { readSubCapitols } = client.readQuery({
            query: GET_SUB_CAPITOLS_BY_IDS,
            variables: {
              ids: option?.data ?? [],
            },
          });

          if (readSubCapitols.length === 0) {
            await fetchDefaultData();
            const { readSubCapitols } = client.readQuery({
              query: GET_SUB_CAPITOLS_BY_IDS,
              variables: {
                ids: option?.data ?? [],
              },
            });
            setValue('subCapitols', readSubCapitols);
          } else {
            setValue('subCapitols', readSubCapitols);
          }
          subCapitolDropDownRef.current?.clearSelected();

          elementOrdersDropDownRef.current?.clearSelected();
          setValue('editionSubCapitol', '');
          setValue('editionElementOrder', '');
          setValue('elementOrders', []);
        } catch (err) {
          Sentry.captureException(err);
          console.log(err);
        }
        onChange(option?.value ?? '');
      };
      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const onSelectSubCapitol = useCallback(
    (
      option: OptionSelected | undefined,

      onChange: (...event: any[]) => void
    ) => {
      const tasks = async () => {
        try {
          const { readElementOrders } = client.readQuery({
            query: GET_ELEMENT_ORDERS_BY_ID,
            variables: {
              ids: option?.data ?? [],
            },
          });
          if (readElementOrders.length === 0) {
            await fetchDefaultData();
            const { readElementOrders } = client.readQuery({
              query: GET_ELEMENT_ORDERS_BY_ID,
              variables: {
                ids: option?.data ?? [],
              },
            });

            setValue('elementOrders', readElementOrders);
          } else {
            setValue('elementOrders', readElementOrders);
          }
          elementOrdersDropDownRef.current?.clearSelected();
        } catch (err) {
          Sentry.captureException(err);
          console.log(err);
        }
        onChange(option?.value ?? '');
      };

      setValue('editionElementOrder', '');

      tasks().catch((err) => {
        Sentry.captureException(err);
        console.log(err);
      });
    },
    []
  );

  const renderSubCapitols = useCallback<
    ControllerProps<
      ActionEditionFormValues & WorkOrder,
      'editionSubCapitol'
    >['render']
  >(
    ({ field: { onChange, value }, formState: { defaultValues } }) => {
      const options =
        subCapitols?.map((subCapitol) => ({
          label: subCapitol?.name || '',
          value: subCapitol?.id || '',
          data: subCapitol?.elementOrdersIds || '',
        })) || [];
      return (
        <SearchableDropdown
          ref={subCapitolDropDownRef}
          inputSize="L"
          onChangeSelected={(option) => onSelectSubCapitol(option, onChange)}
          selectedOption={value}
          typeDropDown="Default"
          labelText={t('workOrder.subchapter')}
          placeholder={t('location.selectPlaceholder')}
          defaultValue={defaultValues?.editionSubCapitol}
          options={options}
          borderError={!!errors.editionSubCapitol}
          isDisabled={
            initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
            initialData?.parentExpeditionOrderStatus === OrderState.End ||
            initialData?.state === OrderState.Annulled
          }
        />
      );
    },
    // `errors` en las dependencias (fix 2026-08-05): sin él este render se
    // memoiza con el `errors` del PRIMER render (vacío), así que
    // `borderError` era siempre false y el Subcapítulo NUNCA se pintaba en
    // rojo — el envío se bloqueaba sin decir por qué. Es el único render de
    // este formulario al que le faltaba (su vecino `renderElementOrders` ya
    // lo tenía).
    [subCapitols, errors]
  );
  const renderElementOrders = useCallback<
    ControllerProps<
      ActionEditionFormValues & WorkOrder,
      'editionElementOrder'
    >['render']
  >(
    ({ field: { onChange, value }, formState: { defaultValues } }) => {
      const options =
        elementOrders?.map((elementOrder) => ({
          label: elementOrder?.name || '',
          value: elementOrder?.id || '',
          data: elementOrder?.subCapitolIDs || '',
        })) || [];
      return (
        <SearchableDropdown
          ref={elementOrdersDropDownRef}
          labelText={t('workOrder.element')}
          placeholder={t('location.selectPlaceholder')}
          inputSize={'L'}
          options={options}
          typeDropDown={'Default'}
          defaultValue={defaultValues?.editionElementOrder}
          onChangeSelected={(option) => onChange(option?.value ?? '')}
          selectedOption={value}
          borderError={!!errors.editionElementOrder}
          isDisabled={
            initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
            initialData?.parentExpeditionOrderStatus === OrderState.End ||
            initialData?.state === OrderState.Annulled
          }
        />
      );
    },
    [elementOrders, errors]
  );

  const switchStateValue = () => {
    const { editionAssetSwitch } = initialData;

    if (editionAssetSwitch) {
      return editionAssetSwitch === 'true';
    }

    return false;
  };
  const handleChange = () => {
    if (switchButtonState) {
      setSwitchButtonState(false);
      setValue('editionAssetSwitch', 'false');
    } else {
      setSwitchButtonState(true);
      setValue('editionAssetSwitch', 'true');
    }
  };

  const addToDataChanged = (item: { id: string; state: string }) => {
    if (namingUpdateForm) {
      const previousData = sessionStorage.getItem(namingUpdateForm)
        ? JSON.parse(
            decompressSessionStorage(
              sessionStorage.getItem(namingUpdateForm) as string
            )
          )
        : {};

      if (!previousData.editionData) {
        previousData.editionData = {
          [DataState.added]: [],
          [DataState.deleted]: [],
        };
      }

      if (item.state === DataState.deleted) {
        const deletedIndex = previousData.editionData[
          DataState.deleted
        ].findIndex((addedItem: { id: string }) => addedItem.id === item.id);
        if (deletedIndex === -1) {
          if (item.id.match(/^[0-9a-fA-F]{24}$/)) {
            previousData.editionData[DataState.deleted].push(item.id);
          }
          const addedIndex = previousData.editionData[
            DataState.added
          ].findIndex((addedItem: { id: string }) => addedItem.id === item.id);
          if (addedIndex !== -1) {
            previousData.editionData[DataState.added].splice(addedIndex, 1);
          }
        }
      } else {
        const addedIndex = previousData.editionData[DataState.added].findIndex(
          (addedItem: { id: string }) => addedItem.id === item.id
        );
        if (addedIndex === -1) {
          previousData.editionData[item.state].push(item);
        }
      }

      sessionStorage.setItem(
        namingUpdateForm as string,
        compressSessionStorage(JSON.stringify(previousData))
      );
    }
  };

  const handleChoosePick = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    setIsLoadingFiles(true);
    if (files) {
      const newImages: Img[] = [];
      const newArrayFiles: CustomFile[] = [];

      for (const file of files) {
        const id = file.name;

        // El descarte de duplicados va ANTES de comprimir. Al revés se
        // comprimía la foto y se tiraba el resultado: antes casi no se notaba
        // (solo se comprimía por encima de 4 MB), pero comprimiendo siempre
        // son ~400 ms de worker desperdiciados por cada foto ya añadida.
        const isDuplicate = selectedFiles.some((img) => img.id === id);
        if (isDuplicate) continue;

        // Compresión SIEMPRE (2560 px / 1,2 MB), no por umbral — ver
        // utils/imageCompression.ts.
        const fileToSave = await compressForUpload(file);

        {
          newImages.push({
            id,
            date: new Date(file.lastModified).toISOString(),
            name: file.name,
          });

          newArrayFiles.push({
            id,
            file: fileToSave,
          });

          if (namingUpdateForm) {
            const itemAdded = {
              id,
              state: DataState.added,
            };
            addToDataChanged(itemAdded);
          }
        }
      }

      setSelectedImages((prevImages) => [...prevImages, ...newImages]);
      setSelectedFiles((prevFiles) => [...prevFiles, ...newArrayFiles]);
    }
    setIsLoadingFiles(false);
    event.target.value = '';
  };
  const handleRemoveImage = (id: string) => {
    setSelectedImages((currentImages) =>
      currentImages.filter((image) => image.id !== id)
    );

    setSelectedFiles((currentFiles) =>
      currentFiles.filter((file) => file.id !== id)
    );

    deleteFilesFromDirectory(OrderType.Action, id);

    if (namingUpdateForm) {
      const itemDeleted = {
        id,
        state: DataState.deleted,
      };
      addToDataChanged(itemDeleted);
    }
  };

  const handleAddSketch = useCallback(async () => {
    try {
      enableSketchDebug();
      const sketchDataRaw = sessionStorage.getItem(
        formActionNaming.IMAGE_CANVAS
      );
      log(
        '💾 handleAddSketch - sketchDataRaw exists: %s, databaseId: %s',
        !!sketchDataRaw,
        sketch?.databaseId
      );

      if (!sketchDataRaw) return;

      // Ver el comentario largo del gemelo en `AccidentAttention.tsx`: el
      // croquis se guarda EN CRUDO, y pasarlo por `decompressSessionStorage`
      // lo destruía en producción y acababa borrándolo al reabrir el modal.
      const sketchData = sketchDataRaw;

      // Use raw data URI for blob conversion (not decompressed) to avoid LZString corruption in production
      const sketchBlob = dataURItoBlob(sketchDataRaw);
      const sketchFile =
        sketchBlob && sketchBlob.size > 0
          ? new File(
              [sketchBlob],
              generateFileName(
                OrderType.Action.toUpperCase(),
                formActionNaming.NEW_EDITION_FORM
              ),
              {
                type: sketchBlob.type,
              }
            )
          : null;

      // 🔴 El fichero de SU croquis en `canvas-action`, con nombre LIBRE, y
      // fuera SOLO el suyo anterior, por nombre (Ruling 15; `sketchFile.ts`).
      // La carpeta la comparten las pestañas y crear y validar actuación:
      // vaciarla entera tiraba el croquis sin guardar de otra pestaña. Se
      // escribe ANTES de apuntarlo en el borrador: el nombre definitivo sale
      // de lo que haya en la carpeta. Con el blob vacío no se guarda fichero
      // (solo se quita el anterior).
      let sketchFileName: string | null = null;
      try {
        if (sketchFile) {
          sketchFileName = await saveDraftSketchFile(
            screenImageSource,
            directoryNaming.CANVAS,
            sketchFile,
            sketch?.fileName
          );
        } else {
          await removeDraftSketchFile(
            screenImageSource,
            directoryNaming.CANVAS,
            sketch?.fileName
          );
        }
      } catch (error) {
        // No se pudo escribir: el croquis se ve, pero el borrador dice que no
        // tiene fichero propio y el guardado no sube ninguno (nunca otro).
        log('❌ Error saving sketch file: %O', error);
        sketchFileName = null;
      }

      const newSketch: Sketch = {
        id: sketchData,
        // T56 FIX: Preservar el ID de BD si estamos editando un sketch existente
        databaseId: sketch?.databaseId,
        date: new Date().toISOString(),
        road: initialData?.road ?? '',
        // 🔴 El nombre de SU fichero en `canvas-action`: crear Y validar suben
        // ese y no «el primero de la carpeta», que comparten las pestañas y
        // crear y validar actuación (spec 2026-11-02 wizard-draft-ownership;
        // `sketchFile.ts`). Sin fichero (blob vacío o fallo al escribir),
        // `null` dice «borrador nuevo SIN fichero propio» (no se sube
        // ninguno); sin la marca se confundía con un borrador de antes y caía
        // en «el primero», que podía ser el croquis de otro (revisión de la
        // Task 9).
        fileName: sketchFileName,
      };

      log(
        '💾 handleAddSketch - new sketch: id starts with %s, databaseId: %s',
        sketchData?.substring(0, 20),
        newSketch.databaseId
      );

      setSketch(newSketch);

      // Limpiar IMAGE_CANVAS de sessionStorage (el blob ya está en OPFS)
      // T56 FIX: NO borrar SVG_CANVAS ni ARRAY_CANVAS - UploadImage los necesita via getSketchFromSessionStorage
      sessionStorage.removeItem(formActionNaming.IMAGE_CANVAS);
      log(
        '💾 handleAddSketch - IMAGE_CANVAS cleared, SVG/ARRAY preserved for UploadImage, file saved to OPFS'
      );
    } catch (error) {
      log('❌ Error saving sketch: %O', error);
    }
    // `sketch?.fileName`: el fichero propio anterior que hay que quitar al
    // redibujar (sin él, el cierre vería el de antes del último dibujo).
  }, [initialData, sketch?.databaseId, sketch?.fileName]); // T56 FIX: Añadir sketch?.databaseId

  // T56 FIX: Restaurar sketch data antes de abrir modal
  const openSketchModal = async () => {
    enableSketchDebug();
    log('🖊️ openSketchModal - sketch state: %O', {
      hasSketch: !!sketch,
      sketchIdPrefix: sketch?.id?.substring(0, 30),
      isLocalDataUri: sketch?.id?.startsWith('data:'),
      databaseId: sketch?.databaseId,
      name: sketch?.name,
    });

    // T56 FIX: Prioridad de carga del sketch:
    // 1. Si sketch.id es un data URI → fue modificado localmente (no guardado en servidor aún)
    // 2. Si hay databaseId → sketch existente no modificado, restaurar del servidor
    // 3. Si no hay nada → sketch nuevo, limpiar sessionStorage
    if (sketch?.id?.startsWith('data:')) {
      log(
        '📍 Using local data URI (sketch modified but not saved to server yet)'
      );
      sessionStorage.setItem(formActionNaming.IMAGE_CANVAS, sketch.id);
      sessionStorage.setItem(formActionNaming.SVG_CANVAS, '');
      sessionStorage.setItem(formActionNaming.ARRAY_CANVAS, '[]');
    } else if (sketch?.databaseId) {
      try {
        log('🔄 Restoring existing sketch from server: %s', sketch.databaseId);
        const restored = await restoreSketchToSessionStorage(
          sketch.databaseId,
          'action'
        );
        if (restored) {
          log('✅ Sketch data restored successfully');
        } else {
          log('⚠️ Could not restore sketch data');
        }
      } catch (error) {
        log('❌ Error restoring sketch: %O', error);
        // Continue opening the modal even if restoration fails
      }
    } else {
      log('🧹 No sketch data found - clearing sessionStorage');
      clearSketchFromSessionStorage('action');
    }

    dispatch(
      openModal(modalIds.ADD_ACTION_SKETCH_MODAL, {
        handleAddSketch: () => {
          handleAddSketch();
          dispatch(closeModal());
        },
      })
    );
  };

  const handleRemoveSketch = async () => {
    try {
      setSketch(null);
      // Solo el fichero de ESTE borrador, por nombre (Ruling 15): el resto de
      // `canvas-action` es de otras pestañas o del otro asistente.
      await removeDraftSketchFile(
        screenImageSource,
        directoryNaming.CANVAS,
        sketch?.fileName
      );

      if (namingUpdateForm && sketch) {
        const itemDeleted = {
          id: sketch.id,
          state: DataState.deleted,
        };
        addToDataChanged(itemDeleted);
      }
    } catch (error) {
      log('❌ Error removing sketch: %O', error);
    }
  };

  const handleOpenCamera = () => {
    dispatch(
      openModal(modalIds.OPEN_CAMERA_MODAL, {
        setImagesState: setSelectedImages,
        setFilesState: setSelectedFiles,
        type: OrderType.Action.toUpperCase(),
        dataBaseNamingCaching: formActionNaming.NEW_ACTION_DATA,
        // FIX (pre-existente): registrar la foto de cámara en DATA_CHANGES, igual
        // que el selector de archivos (handleChoosePick). Sin esto, una foto
        // tomada con la cámara en EDICIÓN se veía pero no se guardaba
        // (editionData vacío → imagesToAdd vacío → UploadImage no corría).
        onImageAdded: (image: { id: string }) =>
          addToDataChanged({ id: image.id, state: DataState.added }),
      })
    );
  };

  const handleOpenModal = () => {
    dispatch(
      openModal(modalIds.ADD_MULTIMEDIA_MODAL, {
        handleOpenCamera: handleOpenCamera,
        handleChoosePick: () => {
          inputRef?.current?.click();
          dispatch(closeModal());
        },
      })
    );
  };

  return (
    <GeneralContainer onSubmit={handleSubmit(onFormSubmit)}>
      <Title component="h3" variant="semiBold" size="L">
        {t('actionEdition.title')}
      </Title>
      <InputsContainer>
        <RowsContainerLeft>
          {/* UNA sola fila que se empaqueta sola, en vez de filas cortadas a
              mano (2026-08-17).

              El orden del DOM ES la cascada: Predefinido → Capítulo →
              Subcapítulo → Elemento → Acción, con lo que no es cascada
              —Activos y Corte de carril— arriba, pegado a Predefinidos. Elegir
              un Predefinido reescribe todo lo que va por debajo, y BORRA la
              Acción si no trae actividad (`onSelectPredefined`): por eso ningún
              campo cascadeado puede pintarse por encima de él. «Acción» era el
              PRIMERO de la pantalla y se la machacaban acto seguido (reporte en
              tablet 2026-08-15).

              Por qué un contenedor y no filas fijas: los controles tienen ancho
              fijo por tamaño (Predefinidos 350, los cinco largos 270, el
              interruptor 200, Corte de carril 120, con 32 de hueco), así que
              CUALQUIER corte a mano deja huérfanas a algún ancho — tres de 270
              piden 834 y la columna da 607. Empaquetando en greedy salen filas
              llenas a 1920, 1728, 1512 y 1280 sin tocar nada, y al estrecharse
              se reordena solo. El tope de ancho y el `flex-wrap` están en
              `RowsContainerLeft` (ver el porqué allí: sin tope, esta columna le
              robaba 220 px a «Descripción validada»). */}
          {/* El orden de esta columna ES la cascada: Predefinido → Capítulo →
              Subcapítulo → Elemento → Acción. Elegir un Predefinido reescribe
              todo lo que va por debajo —incluida la Acción, que se BORRA si el
              predefinido no trae actividad (ver `onSelectPredefined`)—, así
              que ningún campo cascadeado puede pintarse por encima de él.

              «Acción» estaba la PRIMERA de la pantalla: el operario la
              rellenaba y el Predefinido se la machacaba acto seguido, sin
              aviso (reporte en tablet 2026-08-15).

              Reparto en tres filas (2026-08-17): lo que NO es cascada
              —Activos y Corte de carril— sube a la primera, a rellenar el
              hueco que dejaba «Predefinidos» solo; las tres filas van de tres,
              tres y dos campos, así que al estrecharse la pantalla caen de una
              en una y en el orden de lectura, en vez de dejar filas a medias.
              `Row` hace `flex-wrap` en tablet: el orden del DOM ES el de
              lectura. */}
            <Controller
              name="editionPredefine"
              control={control}
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.predefinedOrders?.map((predefinedOrder) => ({
                    label: predefinedOrder?.name || '',
                    value: predefinedOrder?.id || '',
                    data: {
                      capitolId: predefinedOrder?.capitolId || '',
                      subCapitolId: predefinedOrder?.subCapitolId || '',
                      elementOrderId: predefinedOrder?.elementOrderId || '',
                      activityId: predefinedOrder?.activityId || '',
                    },
                  })) || [];
                return (
                  <SearchableDropdown
                    id="predefine-select"
                    ref={predefineDropDownRef}
                    labelText={t('workOrder.predefined')}
                    placeholder={t('location.selectPlaceholder')}
                    inputSize={'XL'}
                    options={options}
                    typeDropDown={'Default'}
                    onChangeSelected={(option) =>
                      onSelectPredefined(option, onChange)
                    }
                    selectedOption={value}
                    defaultValue={defaultValues?.editionPredefine}
                    borderError={!!errors.editionPredefine}
                    minSearchLength={2}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
            <NormalContainer>
              <SwitchContainer>
                <SimpleSwitch
                  changeState={handleChange}
                  state={switchButtonState}
                  disabled={
                    initialData?.parentExpeditionOrderStatus ===
                      OrderState.Annulled ||
                    initialData?.parentExpeditionOrderStatus ===
                      OrderState.End ||
                    initialData?.state === OrderState.Annulled
                  }
                />
                <Typography size={isLandscapeTablet ? 'XS' : 'S'}>
                  {t('actionEdition.active')}
                </Typography>
              </SwitchContainer>
              <Controller
                control={control}
                name="editionAsset"
                render={({
                  field: { onChange },
                  formState: { defaultValues },
                }) => {
                  const options =
                    defaultValues?.assets?.map((asset) => ({
                      label: asset?.code || '',
                      value: asset?.id || '',
                    })) || [];
                  return (
                    <DropDownInput
                      isDisabled={
                        !switchButtonState ||
                        initialData?.parentExpeditionOrderStatus ===
                          OrderState.Annulled ||
                        initialData?.parentExpeditionOrderStatus ===
                          OrderState.End ||
                        initialData?.state === OrderState.Annulled
                      }
                      options={options}
                      placeholder={''}
                      inputSize={'M'}
                      typeDropDown={'Default'}
                      onChangeSelected={(option) =>
                        onChange(option?.value ?? '')
                      }
                      defaultValue={defaultValues?.editionAsset}
                      borderError={!!errors.editionAsset}
                    />
                  );
                }}
              />
            </NormalContainer>
            <Controller
              control={control}
              name="editionLaneCutting"
              render={({
                field: { onChange },
                formState: { defaultValues },
              }) => {
                const options = [
                  {
                    label: t('actionEdition.yes'),
                    value: 'true',
                  },
                  {
                    label: t('actionEdition.no'),
                    value: 'false',
                  },
                ];

                return (
                  <DropDownInput
                    inputSize="XS"
                    onChangeSelected={(option) => {
                      if (option?.value === 'true') {
                        return onChange('true');
                      } else {
                        return onChange('false');
                      }
                    }}
                    typeDropDown="Default"
                    labelText={t('workOrder.laneCut')}
                    placeholder={t('location.selectPlaceholder')}
                    defaultValue={defaultValues?.editionLaneCutting}
                    borderError={!!errors?.editionLaneCutting}
                    options={options}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
            <Controller
              name="editionCapitol"
              control={control}
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.capitols?.map((capitol) => ({
                    label: capitol?.name || '',
                    value: capitol?.id || '',
                    data: capitol?.subCapitolsIDs || [],
                  })) || [];
                return (
                  <SearchableDropdown
                    labelText={t('workOrder.chapter')}
                    placeholder={t('location.selectPlaceholder')}
                    inputSize={'L'}
                    options={options}
                    typeDropDown={'Default'}
                    onChangeSelected={(option) =>
                      onSelectCapitol(option, onChange)
                    }
                    selectedOption={value}
                    ref={capitolDropDownRef}
                    defaultValue={defaultValues?.editionCapitol}
                    borderError={!!errors.editionCapitol}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
            <Controller
              name="editionSubCapitol"
              control={control}
              render={renderSubCapitols}
            />
            <Controller
              name="editionElementOrder"
              control={control}
              render={renderElementOrders}
            />
            {/* Última de la cascada: por debajo de Elemento y de Subcapítulo,
                que es justo lo que la reescribe. Ver el comentario de arriba. */}
            <Controller
              name="editionActivity"
              control={control}
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.activitys?.map((activity) => ({
                    label: activity?.name || '',
                    value: activity?.id || '',
                  })) || [];
                return (
                  <SearchableDropdown
                    ref={activitysDropDownRef}
                    labelText={t('workOrder.action')}
                    placeholder={t('location.selectPlaceholder')}
                    inputSize={'L'}
                    options={options}
                    typeDropDown={'Default'}
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    selectedOption={value}
                    defaultValue={defaultValues?.editionActivity}
                    borderError={!!errors.editionActivity}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
            {/* Operario que registra (editable, OBLIGATORIO — ver schema).
                Opciones desde defaultValues.operators (cargadas por
                Edition/loader), valor pre-seleccionado desde
                registeredByUserId de la Order. */}
            <Controller
              name="registeredByUserId"
              control={control}
              render={({
                field: { onChange, value },
                formState: { defaultValues },
              }) => {
                const options =
                  defaultValues?.operators?.map((op) => ({
                    label: op?.name || '',
                    value: op?.id || '',
                  })) || [];
                return (
                  <SearchableDropdown
                    id="registered-by-select"
                    labelText={t('registeredBy.label')}
                    placeholder={t('registeredBy.placeholder')}
                    inputSize={'L'}
                    options={options}
                    typeDropDown={'Default'}
                    onChangeSelected={(option) => onChange(option?.value ?? '')}
                    selectedOption={value}
                    defaultValue={defaultValues?.registeredByUserId}
                    borderError={!!errors.registeredByUserId}
                    isDisabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                );
              }}
            />
        </RowsContainerLeft>
        <RowsContainerRight>
          <InputTextArea
            {...register('editionDescription')}
            disabled={
              initialData?.parentExpeditionOrderStatus ===
                OrderState.Annulled ||
              initialData?.parentExpeditionOrderStatus === OrderState.End ||
              initialData?.state === OrderState.Annulled
            }
            label={t('actionEdition.validatedDescription')}
            borderError={!!errors.editionDescription}
          />
          {/* Horario de la Actuació bàsica (plan 2026-10-08, FR-9.3): la básica
              no tiene la pestaña Operarios, que es donde viven las fechas. Lo
              calcula el back con el hueco del tramo; aquí se ve, y gestión lo
              corrige al validar (lo editado a mano ya no se recalcula). */}
          {basicSchedule && (
            <BasicScheduleContainer data-testid="basic-schedule">
              {/* Cómo se calcula, en el «i» del título (12-oct): como párrafo
                  debajo ocupaba una línea entera en cada básica. */}
              <Typography component="h3" variant="semiBold" size="L">
                {t('actionEdition.basicSchedule')}
                <HelpTooltip text={t('actionEdition.basicScheduleHint')} />
              </Typography>
              {recalculateOrderId && (
                <BasicScheduleActions>
                  <MainButton
                    variant="secondary"
                    type="button"
                    text={t('actionEdition.basicScheduleRecalculate')}
                    onClick={() => {
                      void onRecalculateSchedule();
                    }}
                    disabled={isRecalculatingSchedule}
                  />
                </BasicScheduleActions>
              )}
              <Controller
                control={control}
                name="registerInitDate"
                render={({ field: { onChange, value } }) => (
                  <div data-testid="basic-schedule-start">
                    <DateTime
                      key={`basic-start-${scheduleVersion}`}
                      label={t('workOrder.startDate')}
                      labelHour={t('workOrder.hour')}
                      onChangeDate={onBasicScheduleChange(onChange, value)}
                      // El valor del campo (no `initialData`): tras recalcular
                      // se remonta con la hora nueva.
                      initialDate={(value as string) || undefined}
                      borderError={!!errors.registerInitDate}
                      isDisabled={!basicScheduleEditable}
                    />
                  </div>
                )}
              />
              {/* Hora de llegada (12-oct): igual que en Operarios de la
                  actuación normal — opcional, sin calcular (vacía hasta que
                  alguien la pone), entre inicio y fin y anterior al fin. */}
              <Controller
                control={control}
                name="registerArrivalDate"
                render={({ field: { onChange, value } }) => (
                  <div data-testid="basic-schedule-arrival">
                    <DateTime
                      label={t('workOrder.arrivalDate')}
                      labelHour={t('workOrder.hour')}
                      // '' (no undefined): el borrado con la papelera tiene
                      // que sobrevivir al sessionStorage y viajar como «borrar».
                      onChangeDate={onBasicScheduleChange(onChange, value)}
                      initialDate={initialData.registerArrivalDate || undefined}
                      borderError={!!errors.registerArrivalDate}
                      // Con texto, como el fin: «La fecha de llegada debe ser
                      // anterior a la fecha final».
                      error={errors.registerArrivalDate?.message}
                      clearable
                      isDisabled={!basicScheduleEditable}
                    />
                  </div>
                )}
              />
              <Controller
                control={control}
                name="registerEndDate"
                render={({ field: { onChange, value } }) => (
                  <div data-testid="basic-schedule-end">
                    <DateTime
                      key={`basic-end-${scheduleVersion}`}
                      label={t('workOrder.endDate')}
                      labelHour={t('workOrder.hour')}
                      // '' (no undefined) para que el borrado con la papelera
                      // sobreviva al sessionStorage y viaje como «borrar».
                      onChangeDate={onBasicScheduleChange(onChange, value)}
                      initialDate={(value as string) || undefined}
                      borderError={!!errors.registerEndDate}
                      // Con texto, no solo el borde rojo: «La fecha final debe
                      // ser posterior a la fecha de inicio» (la de Operarios).
                      error={errors.registerEndDate?.message}
                      clearable
                      isDisabled={!basicScheduleEditable}
                    />
                  </div>
                )}
              />
            </BasicScheduleContainer>
          )}
          <SketchImagesContainer>
            <SketchContainer>
              <Typography component="h3" variant="semiBold" size="L">
                {t('actionEdition.sketch')}
              </Typography>
              {!sketch ? (
                <MainButton
                  text={t('actionEdition.addSketch')}
                  type="button"
                  onClick={openSketchModal}
                  disabled={
                    initialData?.parentExpeditionOrderStatus ===
                      OrderState.Annulled ||
                    initialData?.parentExpeditionOrderStatus ===
                      OrderState.End ||
                    initialData?.state === OrderState.Annulled
                  }
                />
              ) : (
                <ContainerInputLoads>
                  <InputLoadFiles
                    id={sketch.id}
                    date={sketch.date}
                    road={sketch.road ?? ''}
                    name={sketch.name ?? 'Action'}
                    onRemove={handleRemoveSketch}
                  />
                  <ButtonIconBackground
                    variant="edit"
                    type="button"
                    onClick={openSketchModal}
                    disabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                </ContainerInputLoads>
              )}
            </SketchContainer>
            <ImagesContainer>
              <Typography component="h3" variant="semiBold" size="L">
                {t('actionEdition.photos')}
              </Typography>
              {selectedImages.length === 0 ? (
                !isLoadingFiles ? (
                  <MainButton
                    text={t('actionEdition.addImages')}
                    type="button"
                    onClick={handleOpenModal}
                    disabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                ) : (
                  <Loader />
                )
              ) : (
                <ContainerInputLoads>
                  <InputLoadImages
                    imageData={selectedImages}
                    removeItem={handleRemoveImage}
                    disabled={
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.Annulled ||
                      initialData?.parentExpeditionOrderStatus ===
                        OrderState.End ||
                      initialData?.state === OrderState.Annulled
                    }
                  />
                  <Row>
                    {/* Ver el gemelo en `AccidentAttention.tsx`: con fotos ya
                        añadidas esta rama no pintaba ningún indicador, así que
                        añadir más solo bloqueaba los botones y parecía un
                        cuelgue. */}
                    {isLoadingFiles ? (
                      <InlineLoaderSlot>
                        <Loader />
                      </InlineLoaderSlot>
                    ) : (
                      <ButtonIconBackground
                        variant="add"
                        type="button"
                        onClick={handleOpenModal}
                        disabled={
                          initialData?.parentExpeditionOrderStatus ===
                            OrderState.Annulled ||
                          initialData?.parentExpeditionOrderStatus ===
                            OrderState.End ||
                          initialData?.state === OrderState.Annulled
                        }
                      />
                    )}
                  </Row>
                </ContainerInputLoads>
              )}
            </ImagesContainer>
          </SketchImagesContainer>
          <HiddenInput
            type="file"
            accept="image/*"
            multiple
            ref={inputRef}
            onChange={handleChoosePick}
          />
        </RowsContainerRight>
      </InputsContainer>

      <ButtonContainer>
        <MainButton
          text={
            initialData?.parentExpeditionOrderStatus === OrderState.Annulled ||
            initialData?.parentExpeditionOrderStatus === OrderState.End ||
            initialData?.state === OrderState.Annulled
              ? t('actionEdition.continue')
              : textButton
          }
          type="submit"
          disabled={isLoadingFiles}
        />
      </ButtonContainer>
    </GeneralContainer>
  );
};

export default EditingActionForm;
